/* iter567 — Doctor Share Section (Patient side).
 *
 * Lives in ProfilePage. Patient generates a 7-day invite link for a specific
 * doctor email, sees pending claims awaiting approval, and manages active
 * approved doctors.
 *
 * Flow stages visible to patient:
 *   - invited  (just created, waiting for doctor to register/login)
 *   - claimed  (doctor claimed → patient must approve)
 *   - approved (doctor has access; patient can revoke)
 *   - revoked / expired (read-only history)
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Stethoscope, Mail, Copy, Check, X, Clock, ShieldCheck, Loader2 } from "lucide-react";
import { useAuth } from "@/App";
import { toast } from "sonner";

const STATUS_BG = {
  invited: { label: "Изпратен", color: "stone", icon: Clock },
  claimed: { label: "Чака одобрение", color: "amber", icon: Stethoscope },
  approved: { label: "Активен", color: "emerald", icon: ShieldCheck },
  revoked: { label: "Отказан", color: "rose", icon: X },
  expired: { label: "Изтекъл", color: "stone", icon: Clock },
};

const PILL = {
  stone: "bg-stone-100 text-stone-700 border-stone-200",
  amber: "bg-amber-100 text-amber-800 border-amber-200",
  emerald: "bg-emerald-100 text-emerald-800 border-emerald-200",
  rose: "bg-rose-100 text-rose-700 border-rose-200",
};

export default function DoctorShareSection() {
  const { API } = useAuth();
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [copiedToken, setCopiedToken] = useState(null);

  const getToken = () =>
    localStorage.getItem("nutramilo_access_token") ||
    sessionStorage.getItem("nutramilo_access_token");

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/doctor-share/my-invites`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setInvites(r.data?.items || []);
    } catch {
      setInvites([]);
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => {
    reload();
  }, [reload]);

  const createInvite = async (e) => {
    e?.preventDefault();
    if (!email.trim()) {
      toast.error("Въведи email на лекаря.");
      return;
    }
    setCreating(true);
    try {
      await axios.post(
        `${API}/doctor-share/invite`,
        {
          doctor_email: email.trim(),
          doctor_name: name.trim() || null,
          note: note.trim() || null,
        },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success("Поканата е създадена. Сподели линка с лекаря.");
      setEmail("");
      setName("");
      setNote("");
      await reload();
    } catch (e2) {
      toast.error(
        e2?.response?.data?.detail || "Грешка при създаване на покана."
      );
    } finally {
      setCreating(false);
    }
  };

  const copyLink = async (token) => {
    const url = `${window.location.origin}/share/doctor/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedToken(token);
      toast.success("Линкът е копиран. Изпрати го на лекаря.");
      setTimeout(() => setCopiedToken(null), 2500);
    } catch {
      toast.error("Не успях да копирам. Маркирай ръчно.");
    }
  };

  const approveInvite = async (id) => {
    try {
      await axios.post(
        `${API}/doctor-share/${id}/approve`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success("Одобри лекаря. Той вече има достъп.");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка при одобряване.");
    }
  };

  const revokeInvite = async (id) => {
    if (!window.confirm("Сигурен ли си, че искаш да оттеглиш достъпа?")) return;
    try {
      await axios.post(
        `${API}/doctor-share/${id}/revoke`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success("Достъпът е оттеглен.");
      reload();
    } catch {
      toast.error("Грешка при отказ.");
    }
  };

  return (
    <section
      data-testid="doctor-share-section"
      className="bg-white border border-stone-200 rounded-3xl p-6 sm:p-8"
    >
      <header className="flex items-start gap-3 mb-5">
        <div className="w-11 h-11 rounded-2xl bg-violet-100 flex items-center justify-center shrink-0">
          <Stethoscope className="w-5 h-5 text-violet-700" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-bold text-stone-900">Сподели с лекар</h2>
          <p className="mt-1 text-sm text-stone-600 leading-relaxed">
            Изпрати read-only достъп до твоите данни на лекар. Той се
            регистрира еднократно, ти одобряваш — той вижда твоите индекси, без
            да може да променя нищо. Можеш да оттеглиш достъпа по всяко време.
          </p>
        </div>
      </header>

      {/* Create form */}
      <form
        onSubmit={createInvite}
        className="space-y-3 p-4 rounded-2xl bg-stone-50 border border-stone-200"
        data-testid="doctor-share-form"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label htmlFor="ds-email" className="text-xs font-semibold text-stone-700">
              Email на лекаря <span className="text-rose-600">*</span>
            </label>
            <input
              id="ds-email"
              type="email"
              required
              data-testid="doctor-share-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="dr.ivanov@example.com"
              className="mt-1 w-full px-3 py-2 rounded-lg border border-stone-300 bg-white text-stone-900 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
            />
          </div>
          <div>
            <label htmlFor="ds-name" className="text-xs font-semibold text-stone-700">
              Име <span className="text-stone-400">(незадължително)</span>
            </label>
            <input
              id="ds-name"
              type="text"
              data-testid="doctor-share-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="д-р Иванов"
              className="mt-1 w-full px-3 py-2 rounded-lg border border-stone-300 bg-white text-stone-900 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
            />
          </div>
        </div>
        <div>
          <label htmlFor="ds-note" className="text-xs font-semibold text-stone-700">
            Бележка <span className="text-stone-400">(незадължително)</span>
          </label>
          <input
            id="ds-note"
            type="text"
            data-testid="doctor-share-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="„За консултация на 15-ти юли"
            maxLength={500}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-stone-300 bg-white text-stone-900 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
          />
        </div>
        <button
          type="submit"
          disabled={creating}
          data-testid="doctor-share-create"
          className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-full bg-stone-900 text-white font-semibold text-sm hover:bg-stone-800 disabled:opacity-60 disabled:cursor-wait transition-colors"
        >
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
          {creating ? "Създавам…" : "Създай покана"}
        </button>
      </form>

      {/* Invites list */}
      <div className="mt-5 space-y-2.5">
        <h3 className="text-sm font-bold text-stone-900">Активни и минали покани</h3>
        {loading ? (
          <div className="text-center py-6">
            <Loader2 className="w-5 h-5 animate-spin text-stone-400 mx-auto" />
          </div>
        ) : invites.length === 0 ? (
          <div className="text-center py-6 text-sm text-stone-500" data-testid="doctor-share-empty">
            Все още нямаш изпратени покани.
          </div>
        ) : (
          invites.map((inv) => {
            const st = STATUS_BG[inv.status] || STATUS_BG.invited;
            const StatusIcon = st.icon;
            return (
              <div
                key={inv.id}
                data-testid={`doctor-share-item-${inv.id}`}
                className="p-3 rounded-xl bg-white border border-stone-200 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold text-stone-900 truncate">
                      {inv.doctor_name || inv.doctor_email}
                    </span>
                    <span
                      className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${PILL[st.color]}`}
                    >
                      <StatusIcon className="w-3 h-3" />
                      {st.label}
                    </span>
                  </div>
                  <div className="text-xs text-stone-500 mt-0.5 truncate">{inv.doctor_email}</div>
                  {inv.note && (
                    <div className="text-xs text-stone-600 mt-1 italic truncate">„{inv.note}&rdquo;</div>
                  )}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {inv.status === "invited" && (
                    <button
                      type="button"
                      onClick={() => copyLink(inv.token)}
                      data-testid={`doctor-share-copy-${inv.id}`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-stone-100 hover:bg-stone-200 text-stone-700 text-xs font-semibold transition-colors"
                    >
                      {copiedToken === inv.token ? (
                        <Check className="w-3.5 h-3.5 text-emerald-600" />
                      ) : (
                        <Copy className="w-3.5 h-3.5" />
                      )}
                      Копирай линка
                    </button>
                  )}
                  {inv.status === "claimed" && (
                    <button
                      type="button"
                      onClick={() => approveInvite(inv.id)}
                      data-testid={`doctor-share-approve-${inv.id}`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold transition-colors"
                    >
                      <Check className="w-3.5 h-3.5" />
                      Одобри
                    </button>
                  )}
                  {(inv.status === "invited" || inv.status === "claimed" || inv.status === "approved") && (
                    <button
                      type="button"
                      onClick={() => revokeInvite(inv.id)}
                      data-testid={`doctor-share-revoke-${inv.id}`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white hover:bg-rose-50 border border-stone-200 hover:border-rose-300 text-rose-700 text-xs font-semibold transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                      Откажи
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}
